import { EventEmitter, Injectable } from '@angular/core';
import { ApiService, GenericResponsePage } from '@uranda/commons';
import { JoinPartner } from '../../../../models/requests/partner-join.model';
import { HttpParams } from '@angular/common/http';
import { ContactRequest, ContactStatus } from '../../../../models/contact-request.model';
import { VerifyPartnerRequest } from '../../../../models/requests/verify-partner.model';
import { PartnerOfferRequestDetail, PartnerOfferRequestFilter, PartnerQuoteRequest } from '../../../../models/partner-quote-request.model';
import { Offer } from '../../../../models/offer.model';
import { Quote } from '../../../../models/quote.model';
import { ContactRequestFilter } from '../../../../models/requests/contactRequestFilter';
import { Partner } from '../../../../models/partner.model';
import { UpdatePartnerRequest } from '../../../../models/requests/update-partner-request.model';
import { DashboardCounters } from '../../../../models/dashboardCounters.model';
import { ActionRequest } from '../../../../models/action.request';
import { PartnerContactRequest } from '../../../../models/partner-contact-request.model';
import { LeadAssignmentStatusEnum } from '../../../../models/lead-assignment-status.enum';
import { CreateOffer } from '../../../../models/requests/create-offer.model';
import { RequestLabelEnum } from '../../../../models/requests/createLeadRequest.model';
import { ColumnSorting, SortingUtilsService } from '@uranda/ui';
import { Invoice, InvoiceFilters } from '../../../../models/invoices.model';
import { WalletService } from '../../../../../services/wallet.service';
import { ProfileService } from '../../../../auth/services/profile.service';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PartnerService extends ApiService {
  sortingUtils = this.injector.get(SortingUtilsService);

  walletService = this.injector.get(WalletService);
  profileService = this.injector.get(ProfileService);

  async register(partnerRequest?: JoinPartner) {
    const payload = {
      name: partnerRequest?.name || '',
      representativeFirstName: partnerRequest?.representativeFirstName || '',
      representativeLastName: partnerRequest?.representativeLastName || '',
      countryId: partnerRequest?.countryId ?? 0,
      regionId: partnerRequest?.regionId ?? 0,
      districtId: partnerRequest?.districtId ?? 0,
      city: partnerRequest?.city ?? 0,
      address: partnerRequest?.address || '',
      homeNumber: partnerRequest?.homeNumber || '',
      zipCode: partnerRequest?.zipCode || '',
      companyName: partnerRequest?.companyName || '',
      vat: partnerRequest?.vat || '',
      phoneNumber: partnerRequest?.phoneNumber || '',
      whatsapp: partnerRequest?.whatsapp || '',
      email: partnerRequest?.email || '',
      languages: partnerRequest?.languagesSpoken || [],
      website: partnerRequest?.website || '',
      language: partnerRequest?.language,
      termsAndConditionsAccepted: partnerRequest?.termsAndConditionsAccepted ?? false,
    };

    const response = await this.post('/v1/partners/register', payload);
    return response.data;
  }

  /**
   * Pre-controllo di unicità usato dal wizard di registrazione clinica.
   * Passa solo i campi valorizzati; il backend valuta solo quelli ricevuti.
   * VAT verificato sui Partner (qualsiasi stato), email/telefono sugli Account;
   * telefono/WhatsApp anche tra i numeri già usati da altre cliniche.
   */
  async checkPartnerAvailability(params: {
    vat?: string;
    email?: string;
    phoneNumber?: string;
    whatsapp?: string;
  }): Promise<{
    vatTaken: boolean | null;
    emailTaken: boolean | null;
    phoneTaken: boolean | null;
    whatsappTaken?: boolean | null;
  }> {
    let queryParameters = new HttpParams();
    if (params.vat) {
      queryParameters = queryParameters.append('vat', params.vat);
    }
    if (params.email) {
      queryParameters = queryParameters.append('email', params.email);
    }
    if (params.phoneNumber) {
      queryParameters = queryParameters.append('phoneNumber', params.phoneNumber);
    }
    if (params.whatsapp) {
      queryParameters = queryParameters.append('whatsapp', params.whatsapp);
    }
    const response = await this.get('/v1/partners/availability', queryParameters);
    return response.data;
  }

  async getPartnerById(id?: number): Promise<Partner> {
    return await (await this.get('/v1/partners/' + id)).data;
  }

  async unlockPhoneNumber(id: string, last3Digits: string): Promise<void> {
    await this.post('/v1/partner/requests/' + id + '/unlock', { phoneLastDigits: last3Digits });
    // GET /v1/wallet/debt requires AUTHORIZE_PAYMENTS; members without it can still unlock (MANAGE_REQUESTS).
    if (this.profileService.meets('AUTHORIZE_PAYMENTS')) {
      this.walletService.getWallet();
    }
    this.onRequestsUpdated.emit();

  }

  async acceptRequest(id?: string): Promise<void> {
    await this.post('/v1/partner/requests/' + id + '/accept');
    this.onRequestsUpdated.emit();
  }

  async rejectRequest(id?: string): Promise<void> {
    await this.post('/v1/partner/requests/' + id + '/reject');
    this.onRequestsUpdated.emit();
  }

  async acquireQuote(quoteId?: number): Promise<void> {
    await this.post(`/v1/partner/quotes/${quoteId}/acquire`);
    this.onRequestsUpdated.emit();
  }

  async getContactRequests(
    page?: number,
    status?: ContactStatus,
    filters?: ContactRequestFilter,
    size: number = 25,
    q?: string,
    currentSort?: ColumnSorting[]
  ): Promise<GenericResponsePage<ContactRequest[]>> {
    let queryParameters = new HttpParams()
      .append('size', size)
      .append('q', q ?? '');

    if (page) {
      queryParameters = queryParameters.append('page', page);
    }
    if (status) {
      queryParameters = queryParameters.append('status', status);
    }

    if (filters) {
      if (filters.assignedDateFrom) {
        queryParameters = queryParameters.append(
          'assignedDateFrom',
          filters.assignedDateFrom
        );
      }
      if (filters.assignedDateTo) {
        queryParameters = queryParameters.append(
          'assignedDateTo',
          filters.assignedDateTo
        );
      }
    }
    if (currentSort && currentSort.length !== 0) {
      queryParameters = queryParameters.append('orderBy', currentSort[0].attributeName ?? '');
      queryParameters = queryParameters.append('direction', currentSort[0].direction ?? '');
    }
    const response = (
      await this.get('/v1/partner/contact-requests', queryParameters)
    ).data;
    return response;
  }

  async verifyPartner(request?: VerifyPartnerRequest): Promise<void> {
    if (!request) return;
    const formData = new FormData();

    if (
      request.licensePhoto instanceof FileList &&
      request.licensePhoto.length
    ) {
      const original = request.licensePhoto[0];

      const ext = original.type.split('/')[1] || 'jpg';
      const file = new File([original], `licensePhoto.${ext}`, {
        type: original.type,
      });
      formData.append('licensePhoto', file);
    }

    if (
      (request.licensePhoto instanceof FileList ||
        Array.isArray(request.licensePhoto)) &&
      request.licensePhoto.length
    ) {
      const original = request.licensePhoto[0];
      const ext = original.type.split('/')[1] || 'jpg';
      const file = new File([original], `licensePhoto.${ext}`, {
        type: original.type,
      });
      formData.append('licensePhoto', file);
    }

    if (
      (request.cover instanceof FileList || Array.isArray(request.cover)) &&
      request.cover.length
    ) {
      const original = request.cover[0];
      const ext = original.type.split('/')[1] || 'jpg';
      const file = new File([original], `cover.${ext}`, {
        type: original.type,
      });
      formData.append('cover', file);
    }

    if (request.images && request.images.length) {
      const files = Array.from(request.images as FileList | File[]);
      files.forEach((file) => {
        formData.append('images', file);
      });
    }

    if (request.languagesSpoken && request.languagesSpoken.length) {
      request.languagesSpoken.forEach((language) => {
        formData.append('languages', language);
      });
    }

    if (request.fbLink) formData.append('fbLink', request.fbLink);
    if (request.igLink) formData.append('igLink', request.igLink);
    if (request.liLink) formData.append('liLink', request.liLink);
    if (request.ytLink) formData.append('ytLink', request.ytLink);
    if (request.website) formData.append('website', request.website);
    if (request.twitterLink)
      formData.append('twitterLink', request.twitterLink);

    await this.post('/v1/partner/verify-request', formData);
  }

  async getNewOfferRequests(
    page = 0,
    statuses?: LeadAssignmentStatusEnum[],
    filters?: PartnerOfferRequestFilter,
    size = 25,
    q = '',
    currentSort?: ColumnSorting[]
  ): Promise<GenericResponsePage<PartnerQuoteRequest[]>> {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters
      .append('page', page)
      .append('size', size)
      .append('q', q);

    if (statuses) {
      statuses.forEach((status) => {
        queryParameters = queryParameters.append('status', status);
      });
    }

    if (filters) {
      if (filters.assignedDateFrom) {
        queryParameters = queryParameters.append(
          'assignedDateFrom',
          filters.assignedDateFrom
        );
      }
      if (filters.assignedDateTo) {
        queryParameters = queryParameters.append(
          'assignedDateTo',
          filters.assignedDateTo
        );
      }
      if (filters.treatmentType && Array.isArray(filters.treatmentType)) {
        queryParameters = queryParameters.append(
          'treatmentType',
          filters.treatmentType.join(',')
        );
      }
      if (filters.dentalArch && Array.isArray(filters.dentalArch)) {
        queryParameters = queryParameters.append(
          'dentalArch',
          filters.dentalArch.join(',')
        );
      }
      if (
        filters.requestMotivation &&
        Array.isArray(filters.requestMotivation)
      ) {
        queryParameters = queryParameters.append(
          'requestMotivation',
          filters.requestMotivation.join(',')
        );
      }
    }
    if (currentSort && currentSort.length !== 0) {
      queryParameters = queryParameters.append('orderBy', currentSort[0].attributeName ?? '');
      queryParameters = queryParameters.append('direction', currentSort[0].direction ?? '');
    }
    const response = (await this.get('/v1/partner/quote-requests', queryParameters)).data;
    if (response?.content) {
      response.content = response.content.map((r: PartnerQuoteRequest) => ({
        ...r,
        quoteRequest: this.applyOfferRequestArchivedDefaults(r.quoteRequest),
      }));
    }
    return response;
  }

  async getQuoteRequest(id: string): Promise<PartnerQuoteRequest> {
    const result = (await this.get('/v1/partner/quote-requests/' + id)).data;
    return { ...result, quoteRequest: this.applyOfferRequestArchivedDefaults(result.quoteRequest) };
  }

  async getContactRequest(leadId?: string): Promise<PartnerContactRequest> {
    return (await this.get('/v1/partner/contact-requests/' + leadId)).data;
  }

  async getQuotes(
    page = 0,
    size = 25,
    onlyInterested = false,
    q = '',
    currentSort?: ColumnSorting[]
  ): Promise<GenericResponsePage<Offer[]>> {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters
      .append('page', page)
      .append('size', size)
      .append('onlyInterested', onlyInterested)
      .append('q', q);

    if (currentSort) {
      this.sortingUtils.appendSortingParams(queryParameters, currentSort);
    }
    return (await this.get('/v1/partner/quotes', queryParameters)).data;
  }

  async getQuoteDetails(id: number): Promise<Quote> {
    return (await this.get(`/v1/partner/quotes/${id}`)).data;
  }

  async sendQuoteDetails(id: number): Promise<Quote> {
    return (await this.post(`/v1/partner/quotes/${id}/acquire`)).data;
  }

  async updatePartner(request: UpdatePartnerRequest): Promise<any> {
    const formData = new FormData();

    if (request.name) formData.append('name', request.name);
    if (request.countryId) formData.append('countryId', request.countryId.toString());
    if (request.regionId) formData.append('regionId', request.regionId.toString());
    if (request.districtId) formData.append('districtId', request.districtId.toString());
    if (request.city) formData.append('city', request.city);
    if (request.address) formData.append('address', request.address);
    // Optional fields the user can clear. Two forms use this endpoint (Settings: contact & location
    // incl. whatsapp; Clinic details: cover, socials, languages) and each request carries only the
    // keys of the form it comes from. A key present in the request is always sent, empty ('') when
    // cleared, so the backend clears it; a key absent from the request is not sent and the backend
    // leaves that field unchanged.
    const appendClearable = (key: keyof UpdatePartnerRequest) => {
      if (Object.prototype.hasOwnProperty.call(request, key)) {
        const value = request[key];
        formData.append(key, value === null || value === undefined ? '' : String(value).trim());
      }
    };
    appendClearable('homeNumber');
    appendClearable('zipCode');
    appendClearable('whatsapp');
    if (request.languagesSpoken && request.languagesSpoken.length) {
      request.languagesSpoken.forEach((language) => {
        formData.append('languages', language);
      });
    }
    appendClearable('twitterLink');
    appendClearable('fbLink');
    appendClearable('igLink');
    appendClearable('liLink');
    appendClearable('ytLink');
    appendClearable('website');
    if (request.companyName)
      formData.append('companyName', request.companyName);
    if (request.representativeFirstName)
      formData.append(
        'representativeFirstName',
        request.representativeFirstName
      );
    if (request.representativeLastName)
      formData.append('representativeLastName', request.representativeLastName);
    if (request.vat) formData.append('vat', request.vat);
    if (request.email) formData.append('email', request.email);
    if (request.phoneNumber)
      formData.append('phoneNumber', request.phoneNumber);

    if (request.cover) {
      let file: File | null = null;

      if (request.cover instanceof File) {
        file = request.cover;
      } else if (
        Array.isArray(request.cover) &&
        (request.cover as Array<File>).length > 0
      ) {
        file = (request.cover as Array<File>)[0];
      } else if ((request.cover as FileList)?.length > 0) {
        file = (request.cover as FileList)[0];
      }

      if (file) {
        const ext = file.type.split('/')[1] || 'jpg';
        const finalFile = new File([file], `cover.${ext}`, { type: file.type });
        formData.append('cover', finalFile);
      }
    }

    return (await this.put('/v1/partner/update', formData)).data;
  }

  async acceptTerms(serviceTypes: ('ORGANIC' | 'SUBSCRIPTION' | 'GENERAL')[]): Promise<void> {
    const query = serviceTypes.map(t => `serviceTypes=${t}`).join('&');
    await this.post(`/v1/partner/accept-terms?${query}`);
  }

  async getCounters(): Promise<DashboardCounters> {
    return (await this.get('/v1/partner/dashboard/counters')).data;
  }

  async getActionRequests(): Promise<ActionRequest[]> {
    return (await this.get('/v1/partner/dashboard/requested-actions')).data;
  }

  async getLastRequests(
    currentSort?: ColumnSorting[]
  ): Promise<ActionRequest[]> {
    let queryParameters = new HttpParams();

    if (currentSort) {
      this.sortingUtils.appendSortingParams(queryParameters, currentSort);
    }
    return (
      await this.get('/v1/partner/dashboard/last-requests', queryParameters)
    ).data;
  }

  async createQuote(offerRequestId: string, offer: CreateOffer): Promise<any> {
    const formData = new FormData();

    formData.append('declaredAmount', offer.declaredAmount.toString());
    formData.append(
      'accommodationIncluded',
      String(offer.accommodationIncluded)
    );
    formData.append('flightIncluded', String(offer.flightIncluded));
    formData.append('isFinanceable', String(offer.isFinanceable));
    formData.append('hasTransportToClinic', String(offer.hasTransportToClinic));

    if (offer.internalNote) {
      formData.append('internalNote', offer.internalNote);
    }
    if (offer.pdfFile) {
      formData.append('pdfFile', offer.pdfFile);
    }

    const response = (
      await this.post(
        `/v1/partner/quote-requests/${offerRequestId}/generate-quote`,
        formData
      )
    ).data;
    this.onRequestsUpdated.emit();
    return response;
  }

  async updateLabel(requestId: number, label: RequestLabelEnum) {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters.append('label', label);
    const response = await this.put(
      `/v1/partner/requests/${requestId}/update-label`,
      queryParameters
    );
    return response;
  }

  async updateQuoteNote(
    assignmentId: number,
    internalNote: string
  ): Promise<void> {
    const payload = {
      internalNote: internalNote,
    };
    await this.put(`/v1/partner/quote-requests/${assignmentId}/quote`, payload);
  }

  async getInvoices(
    page = 0,
    size = 10,
    q: string = '',
    currentSort?: ColumnSorting[],
    filters?: InvoiceFilters
  ): Promise<GenericResponsePage<Invoice[]>> {
    let queryParameters = new HttpParams()
      .append('page', page)
      .append('size', size)
      .append('q', q);

    if (currentSort && currentSort.length !== 0) {
      queryParameters = queryParameters.append('orderBy', currentSort[0].attributeName ?? '');
      queryParameters = queryParameters.append('direction', currentSort[0].direction ?? '');
    }
    console.log('Applying invoice filters:', filters);
    if (filters) {

      if (filters.issuedFrom) {
        queryParameters = queryParameters.append('issuedFrom', filters.issuedFrom);
      }
      if (filters.issuedTo) {
        queryParameters = queryParameters.append('issuedTo', filters.issuedTo);
      }
      if (filters.paidFrom) {
        queryParameters = queryParameters.append('paidFrom', filters.paidFrom);
      }
      if (filters.paidTo) {
        queryParameters = queryParameters.append('paidTo', filters.paidTo);
      }
      if (filters.minAmount !== undefined) {
        queryParameters = queryParameters.append('minAmount', filters.minAmount);
      }
      if (filters.maxAmount !== undefined) {
        queryParameters = queryParameters.append('maxAmount', filters.maxAmount);
      }
    }

    const response = await this.get('/v1/partner/billing/invoices', queryParameters);
    return response.data;
  }

  async getInvoiceById(invoiceId: number): Promise<Invoice> {
    const response = await this.get(`/v1/partner/billing/invoices/${invoiceId}`);
    return response.data;
  }

  async downloadInvoicePdf(invoiceId: number): Promise<Blob> {
    return lastValueFrom(
      this.httpClient.get(
        `${this.getApiUrl()}/v1/partner/billing/invoices/${invoiceId}/pdf`,
        { responseType: 'blob' }
      )
    );
  }

  async getMyQrCode(): Promise<{ qrCodeData: string; partnerName: string }> {
    const response = await this.get('/v1/partner/qr-code');
    return response.data;
  }

  private applyOfferRequestArchivedDefaults(detail: PartnerOfferRequestDetail | undefined): PartnerOfferRequestDetail | undefined {
    if (!detail) return detail;
    // I placeholder (immagine archiviata) vanno mostrati SOLO quando la
    // richiesta è stata effettivamente purgata dalla retention, segnalata dal campo lead.purgedAt.
    // Sulle richieste normali i campi opzionali vuoti devono restare vuoti, non comparire come archiviati.
    // I campi sanitari testuali passano invece da Utils.formatHealthDataField nei componenti che li mostrano.
    if (!detail.lead?.purgedAt) {
      return detail;
    }
    return {
      ...detail,
      panoramicPhoto1UrlOriginal: detail.panoramicPhoto1UrlOriginal ?? 'assets/icons/archived-photo.svg',
      panoramicPhoto1UrlLarge: detail.panoramicPhoto1UrlLarge ?? 'assets/icons/archived-photo.svg',
      panoramicPhoto2UrlLarge: detail.panoramicPhoto2UrlLarge ?? 'assets/icons/archived-photo.svg',
      panoramicPhoto1UrlMedium: detail.panoramicPhoto1UrlMedium ?? 'assets/icons/archived-photo.svg',
      panoramicPhoto2UrlMedium: detail.panoramicPhoto2UrlMedium ?? 'assets/icons/archived-photo.svg',
      panoramicPhoto1UrlThumb: detail.panoramicPhoto1UrlThumb ?? 'assets/icons/archived-photo.svg',
      panoramicPhoto2UrlThumb: detail.panoramicPhoto2UrlThumb ?? 'assets/icons/archived-photo.svg',
    };
  }

  onRequestsUpdated = new EventEmitter();
}
